"use client";

import * as React from "react";
import { type ToastProps as OriginalToastProps, Toast } from "@/components/ui/toast";
import { isExpectedDomRemovalError } from "@/lib/errors";

// Re-exporting ToastProps to be used in other files if needed
export type ToastProps = OriginalToastProps;

interface ToastProviderState {
  toasts: ToastProps[];
  toast: (
    title: string,
    descriptionOrOptions?:
      | string
      | {
          description?: string;
          type?: "success" | "error" | "warning" | "info";
          duration?: number;
          group?: string;
        },
    type?: "success" | "error" | "warning" | "info"
  ) => void;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
  clearToastsBy: (predicate: (toast: ToastProps) => boolean) => void;
}

const ToastContext = React.createContext<ToastProviderState | undefined>(undefined);

interface ToastContainerProps {
  toasts: ToastProps[];
  onRemove: (id: string) => void;
}

function ToastContainer({ toasts, onRemove }: ToastContainerProps) {
  return (
    <div className="fixed top-0 right-0 z-50 p-4 space-y-2">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          {...toast}
          onClose={() => {
            try {
              onRemove(toast.id);
            } catch (error) {
              if (!isExpectedDomRemovalError(error)) {
                console.warn("[ToastContainer] Unexpected error removing toast", error);
              }
            }
          }}
        />
      ))}
    </div>
  );
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastProps[]>([]);

  const addToast = React.useCallback((toast: Omit<ToastProps, "id">) => {
    const id = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2, 11);
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);

  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) => {
      if (prev.length === 0) {
        return prev;
      }
      const toastExists = prev.some((toast) => toast.id === id);
      if (!toastExists) {
        if (process.env.NODE_ENV !== "production") {
          console.debug("[toast-context] removeToast skipped", { id });
        }
        return prev;
      }
      return prev.filter((toast) => toast.id !== id);
    });
  }, []);

  const clearAllToasts = React.useCallback(() => {
    setToasts([]);
  }, []);

  const toast = React.useCallback((
    title: string,
    descriptionOrOptions?:
      | string
      | {
          description?: string;
          type?: "success" | "error" | "warning" | "info";
          duration?: number;
          group?: string;
        },
    type: "success" | "error" | "warning" | "info" = "info"
  ) => {
    if (typeof descriptionOrOptions === "object" && descriptionOrOptions !== null) {
      const { description, type: maybeType = "info", duration, group } = descriptionOrOptions;
      addToast({ title, description, type: maybeType, duration, group });
      return;
    }
    addToast({ title, description: descriptionOrOptions, type });
  }, [addToast]);

  const clearToastsBy = React.useCallback((predicate: (toast: ToastProps) => boolean) => {
    setToasts((prev) => {
      if (prev.length === 0) {
        return prev;
      }
      const filtered = prev.filter((toast) => !predicate(toast));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, toast, removeToast, clearAllToasts, clearToastsBy }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

export function useToastContext() {
  const context = React.useContext(ToastContext);
  if (context === undefined) {
    throw new Error("useToastContext must be used within a ToastProvider");
  }
  return context;
}